import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "next/navigation";
import PropTypes from "prop-types";
import BackToTop from "@/components/backToTop";
import Loader from "@/components/loader";
import FilterContent from "@/components/ProductListing/FilterContent";
import NewPLPCard from "@/components/ProductListing/PLPCards/index";
import PLPOrderBySelect from "@/components/ProductListing/PLPOrderBySelect";
import {
  selectOptions,
  staticLabelsPLP,
  buildFacetObjectFromLabelsAndFields,
  buildFqParams,
  toValueCountArray,
  buildTaggedFilter,
} from "@/components/ProductListing/helper";
import { apim } from "@/constants/api";
import {
  APIM_BASE_URL,
  lwAppName,
  searchPLPEndPoint,
  SEARCH_PDP_FL,
} from "@/constants";
import { formatListingData } from "@/services/server/plpHelper";
import { getPlpProcessedData } from "@/services/server/plpResponseProcessor";
import SpecCard from "@/components/Search/SpecCard";
import ArticleVideos from "../ArticlesVideos";
import ContentTypeFilter from "@/components/Search/ContentTypeFilter";
import PLPHeader from "@/components/ProductListing/PLPHeader";

const SearchProductList = ({
  locale = "en",
  plpData = {},
  facetLabelsState = {},
  categoryConfig = "",
  params = {},
  search = false,
  isLoading = false,
  setIsLoading = () => {},
  totalProductCount = 0,
  setTotalProductCount = () => {},
  activeTab = "",
  tabState = [],
  setTabState = () => {},
  setActiveTab = () => {},
}) => {
  const { params: paramsArr = [] } = params || {};
  const categoryFromUrl = Array.isArray(paramsArr) ? paramsArr[1] : "";
  const categoryResult = categoryFromUrl?.replace(/\+/g, " ");
  const slug = categoryResult
    ? `/results/${paramsArr.join("/")}`
    : `/results?search=${search}`;
  const defaultLocale = locale === "default" ? "en" : locale;
  const apiLocale =
    locale === "default" ? "en" : locale === "vn" ? "vi" : locale;
  const searchParams = useSearchParams();
  const {
    facetlabels = {},
    facetFields = {},
    colorSwatchLabels = {},
    facetRanges = {},
  } = facetLabelsState;

  const { data: plpListingData = [], total = 0 } = plpData;

  const filterFacets = Object.keys(facetlabels).length
    ? buildFacetObjectFromLabelsAndFields(
        facetlabels,
        facetFields,
        colorSwatchLabels,
        facetRanges
      )
    : {};

  const handleIfAnyPreFilters = (facetlabels = {}, facetFields = {}) => {
    const params = new URLSearchParams(searchParams);
    const preFilterObj = Object.fromEntries(params.entries());

    if (!Object.entries(preFilterObj).length)
      return {
        matchedFacetLabels: {},
        preFilterObj: {},
        preselectedSort: "",
        preSelectedFacetCount: {},
      };
    const preselectedSort =
      preFilterObj.orderby || preFilterObj.sort
        ? `${preFilterObj.orderby} ${preFilterObj.sort}`
        : "";
    const matchedFacetLabels = Object.keys(facetlabels)
      .map(key => {
        if (preFilterObj.hasOwnProperty(facetlabels[key])) {
          return { [key]: preFilterObj[facetlabels[key]] };
        }
      })
      .filter(item => item);
    const selectedFacetLabels = {};
    const preSelectedFacetCount = {};
    matchedFacetLabels.forEach((item, index) => {
      const key = Object.keys(item)[0];
      selectedFacetLabels[key] = item[key];
      const countIndex = facetFields[key]?.findIndex(
        countItem => countItem === item[key]
      );
      const count = facetFields[key]?.[countIndex + 1];
      preSelectedFacetCount[facetlabels[key]] = count;
    });

    return {
      matchedFacetLabels: selectedFacetLabels,
      preFilterObj,
      preselectedSort,
      preSelectedFacetCount,
    };
  };

  const {
    matchedFacetLabels = {},
    preFilterObj = {},
    preselectedSort = "",
    preSelectedFacetCount = {},
  } = handleIfAnyPreFilters();

  const hasAnyPrefilters = useRef(
    Boolean(Object.keys(matchedFacetLabels).length || preselectedSort)
  );

  const needApiCall = useRef(false);
  const filtersAppliedRef = useRef(false);
  const productsRef = useRef([]);
  const totalProductCountRef = useRef(total);
  const loadMoreRef = useRef(null);
  const totalArticles = useRef(null);
  const hasMoreArticles = useRef(0);
  const activeTabRef = useRef(null);
  const articlesDataRef = useRef(0);
  const specDataRef = useRef(0);
  const hasMoreSpecData = useRef(0);
  const totalSpecData = useRef(0);
  const totalProductsAsPerTabs = useRef(0);

  const [plpStateData, setPlpStateData] = useState(plpListingData);

  const [filterSelectedOnAction, setFilterSelectedOnAction] = useState(
    matchedFacetLabels || {}
  );
  const [isHovering, setIsHovering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSkuId, setSelectedSkuId] = useState("");
  const [apiRequestBody, setApiRequestBody] = useState(preFilterObj || {});
  const [selectedFilterCount, setSelectedFilterCount] = useState(
    preSelectedFacetCount || {}
  );
  const [mappedFilterFacets, setMappedFilterFacets] = useState(filterFacets);
  const [selectedSortOrder, setSelectedSortOrder] = useState(
    preselectedSort || ""
  );
  const [rows, setRows] = useState(30);
  const [start, setStart] = useState(0);

  const [categoryArr, setCategoryArr] = useState([]);
  const [plpSpecData, setPlpSpecData] = useState([]);
  const [articlesData, setArticlesData] = useState([]);
  const [contentTypeFilterOptions, setContentTypeFilterOptions] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [appliedFiltersCount, setAppliedFiltersCount] =
    useState(totalProductCount);
  const [showMobileContentTypeFilter, setShowMobileContentTypeFilter] =
    useState(false);
  const [totalProductCountForHeader, setTotalProductCountForHeader] =
    useState(0);
  const activeCotentTypeFilterRef = useRef(null);
  const baseContentTypeFilterOptions = useRef([]);
  const dynamicContentTypeFieldRef = useRef("");
  const matchedFacetLabelsRef = useRef(null);
  const isInitalLoadOfTabs = useRef(true);
  const hasMore = useRef(true);

  const handleFilterFromQuery = (filterObjWithAlias = {}, aliasField = "") => {
    const params = new URLSearchParams(window.location.search);
    if (aliasField) {
      params.delete(aliasField);
      aliasField === "orderby" && params.delete("sort");
    }

    Object.keys(filterObjWithAlias).forEach(key => {
      if ((key === "type" || key === "search") && params.has(key)) {
        return;
      }
      const value = filterObjWithAlias[key];
      if (value !== undefined && value !== null) {
        params.set(key, value);
      }
    });
    const queryString = params.toString();
    const newRelativePathQuery = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    window.history.replaceState(null, "", newRelativePathQuery);
  };

  const determineActiveType = () => {
    const params = new URLSearchParams(window.location.search);
    const { type = "" } = Object.fromEntries(params.entries());
    return type;
  };

  const determineActiveTab = (product_s, spec_s, article_s) => {
    const type = determineActiveType();
    if (type === "articles" && article_s?.length) return "ARTICLES AND VIDEOS";
    if (type === "spec" && spec_s?.length) return "SPEC";
    if (type === "products" && product_s?.length) return "PRODUCTS";
    if (product_s?.length) return "PRODUCTS";
    if (spec_s?.length) return "SPEC";
    if (article_s?.length) return "ARTICLES AND VIDEOS";
    return "PRODUCTS";
  };

  const toggleMobileContentTypeFilter = () => {
    setShowMobileContentTypeFilter(prevState => !prevState);
  };

  const modifyTabsCount = (facetFields, needToSetActiveTab = false) => {
    const { article_s = [], spec_s = [], product_s = [] } = facetFields || {};
    const modifiedTabs = [...tabState];

    const type = determineActiveType();

    modifiedTabs[1] && (modifiedTabs[1].count = spec_s.length ? spec_s[1] : 0);
    if (type !== "spec") {
      modifiedTabs[0] &&
        (modifiedTabs[0].count = product_s.length ? product_s[1] : 0);
      totalProductsAsPerTabs.current = product_s.length ? product_s[1] : 0;
    }

    totalSpecData.current = spec_s.length ? spec_s[1] : 0;

    if (needToSetActiveTab) {
      modifiedTabs[0] &&
        (modifiedTabs[0].count = product_s.length ? product_s[1] : 0);
      totalProductsAsPerTabs.current = product_s.length ? product_s[1] : 0;
      modifiedTabs[2] &&
        (modifiedTabs[2].count = article_s.length ? article_s[1] : 0);
      totalArticles.current = article_s.length ? article_s[1] : 0;
      activeTabRef.current = determineActiveTab(product_s, spec_s, article_s);
      setActiveTab(determineActiveTab(product_s, spec_s, article_s));
    } else {
      isInitalLoadOfTabs.current = false;
    }

    const totalCount =
      totalArticles.current +
      totalSpecData.current +
      totalProductsAsPerTabs.current;
    setTotalProductCount(totalCount);

    setTabState(modifiedTabs);
  };

  const prepareProductData = async (data = {}) => {
    const {
      fusion: {
        facet_labels: facetlabels = {},
        color_swatch_labels: colorSwatchLabels = {},
      } = {},
      facet_counts: {
        facet_fields: facetFields = {},
        facet_ranges: facetRanges = {},
      } = {},
      response: { numFound = 0, docs = [] } = {},
    } = data || {};
    const { ProductCategory_s = [], product_s = [] } = facetFields || {};
    const formattedData = await formatListingData(data, false);
    if (!needApiCall.current) {
      setTotalProductCountForHeader(numFound);
      modifyTabsCount(facetFields);
    }
    const plpData = await getPlpProcessedData(
      formattedData,
      false,
      [],
      "",
      {},
      {}
    );
    const { data: plpListData = [] } = plpData || {};

    const filterFacets = buildFacetObjectFromLabelsAndFields(
      facetlabels,
      facetFields,
      colorSwatchLabels,
      facetRanges
    );
    setMappedFilterFacets(filterFacets);
    const isSpec = activeTabRef.current === "SPEC";

    if (needApiCall.current) {
      if (isSpec) {
        const specItemData = docs.filter(item => item?.spec_s);
        setPlpSpecData(prevData => [...prevData, ...specItemData]);
      } else {
        setPlpStateData(prevData => [...prevData, ...plpListData]);
      }
    } else {
      if (isSpec) {
        setPlpSpecData(docs.filter(item => item?.spec_s));
      } else {
        setPlpStateData(plpListData);
      }
    }

    setCategoryArr(toValueCountArray(ProductCategory_s));
    needApiCall.current = false;
    filtersAppliedRef.current = false;
    totalProductCountRef.current = product_s.length ? product_s[1] : 0;
    setAppliedFiltersCount(numFound);
    setIsLoading(false);
  };

  useEffect(() => {
    hasMore.current = productsRef.current.length < totalProductCountRef.current;
    productsRef.current = plpStateData;
  }, [plpStateData]);

  useEffect(() => {
    hasMoreSpecData.current = specDataRef.current < totalSpecData.current;
    specDataRef.current = plpSpecData.length;
  }, [plpSpecData]);

  const executeArticlesAndVideosSearch = (
    start = null,
    rows = 30,
    sortOrderQuery = "",
    contentTypeFilter = ""
  ) => {
    setIsLoading(true);
    const paramsFq = new URLSearchParams();
    if (sortOrderQuery) paramsFq.append("sort", sortOrderQuery);
    paramsFq.append("rows", rows);

    if (start && rows) {
      paramsFq.append("start", start);
    }

    if (contentTypeFilter || activeCotentTypeFilterRef.current) {
      const field = dynamicContentTypeFieldRef.current;
      paramsFq.append(
        "fq",
        `${field}:("${contentTypeFilter.value || activeCotentTypeFilterRef.current}")`
      );
    }

    const profileName = `profile_${lwAppName}_Search`;
    const fq = `article_s:("Yes")`;
    const lang = `language_s:${apiLocale}`;
    const query = `q=${encodeURIComponent(search)}&${paramsFq.toString()}&profilename=${profileName}&fl=${SEARCH_PDP_FL.join(",")}&collections=${lwAppName}&fq=${fq}&fq=${lang}`;
    const url = `${APIM_BASE_URL}${searchPLPEndPoint}?${query}`;
    apim
      .get(url)
      .then(response => {
        if (response?.data) {
          const {
            facet_counts: { facet_fields = {} } = {},
            response: { docs = [] } = {},
          } = response.data || {};

          if (contentTypeFilter) {
            const refinedContentTypeFilterOptions =
              baseContentTypeFilterOptions.current &&
              baseContentTypeFilterOptions.current.length > 1
                ? baseContentTypeFilterOptions.current?.filter(
                    item => item.value !== contentTypeFilter.value
                  )
                : [];
            totalArticles.current = contentTypeFilter.count;
            setContentTypeFilterOptions(refinedContentTypeFilterOptions);
            setSelectedContentType(contentTypeFilter);
          } else {
            const targetEntry = Object.entries(facet_fields).find(
              ([key]) => key !== "article_s"
            );
            const [dynamicFieldForContentTypeFilter] = targetEntry || [];
            dynamicContentTypeFieldRef.current =
              dynamicFieldForContentTypeFilter;
            if (!baseContentTypeFilterOptions.current?.length) {
              const contentTypes = toValueCountArray(targetEntry?.[1] || []);
              baseContentTypeFilterOptions.current = contentTypes;
              setContentTypeFilterOptions(contentTypes);
            }
          }

          if (needApiCall.current) {
            setArticlesData(prevData => {
              const combinedData = [...prevData, ...(docs || [])];
              articlesDataRef.current = combinedData.length;
              hasMoreArticles.current =
                articlesDataRef.current &&
                articlesDataRef.current < totalArticles.current;
              return combinedData;
            });
          } else {
            articlesDataRef.current = docs.length;
            hasMoreArticles.current =
              articlesDataRef.current &&
              articlesDataRef.current < totalArticles.current;
            setArticlesData(docs);
          }
          needApiCall.current = false;
          setIsLoading(false);
        }
      })
      .catch(err => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab === "ARTICLES AND VIDEOS") {
      executeArticlesAndVideosSearch();
    } else if (activeTab === "SPEC" || activeTab === "PRODUCTS") {
      if (
        !isInitalLoadOfTabs.current &&
        matchedFacetLabelsRef.current !== null
      ) {
        executeProductsAndSpecData(matchedFacetLabelsRef.current, activeTab);
      }
    } else {
      setSelectedContentType(null);
      baseContentTypeFilterOptions.current = [];
      activeCotentTypeFilterRef.current = null;
    }
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const executeProductsAndSpecData = (
    matchedFacetLabels = {},
    activeTabParam = ""
  ) => {
    const paramsFq = new URLSearchParams();
    let fqParams = buildFqParams(matchedFacetLabels, apiLocale);

    if (categoryResult) {
      const obj = {
        ProductCategory_s: categoryResult,
      };
      const categoryParams = buildTaggedFilter(obj);
      paramsFq.append("fq", categoryParams);
    }

    fqParams.forEach(fq => paramsFq.append("fq", fq));

    const applicableFilter =
      activeTabParam.toLocaleLowerCase() === "spec"
        ? `spec_s:("Yes")`
        : `product_s:("Yes")`;

    paramsFq.append("fq", applicableFilter);

    const profileName = `profile_${lwAppName}_Search`;
    const rows = 30;
    const query = `q=${encodeURIComponent(search)}&profilename=${profileName}&fl=${SEARCH_PDP_FL.join(",")}&rows=${rows}&collections=${lwAppName}&${paramsFq.toString()}`;
    const url = `${APIM_BASE_URL}${searchPLPEndPoint}?${query}`;
    apim
      .get(url)
      .then(response => {
        if (response?.data) {
          prepareProductData(response.data);
        }
      })
      .catch(err => {
        setIsLoading(false);
      });
  };

  const extractDataAndFillTabs = (data = {}) => {
    const {
      facet_counts: { facet_fields: facetFields = {} } = {},
      fusion: { facet_labels: facetLabels },
      response: { numFound = 0 },
    } = data || {};
    const { product_s = [] } = facetFields || {};
    const isProductAvailable = product_s.length ? product_s[1] : 0;
    setTotalProductCountForHeader(numFound);
    modifyTabsCount(facetFields, true);
    if (isProductAvailable) {
      const { matchedFacetLabels = {} } = handleIfAnyPreFilters(
        facetLabels,
        facetFields
      );
      matchedFacetLabelsRef.current = matchedFacetLabels;
      const type = determineActiveType();
      executeProductsAndSpecData(matchedFacetLabels, type);
      return;
    }
    setIsLoading(false);
  };

  const handleSelectedContentType = (contentType = {}) => {
    activeCotentTypeFilterRef.current = contentType?.value;
    needApiCall.current = false;
    setStart(0);
    executeArticlesAndVideosSearch(0, 30, selectedSortOrder, contentType);
  };

  const executeSearchPLP = useCallback(() => {
    setIsLoading(true);

    const paramsFq = new URLSearchParams();
    let fqParams = buildFqParams({}, apiLocale);
    if (categoryResult) {
      const obj = {
        ProductCategory_s: categoryResult,
      };
      fqParams = buildFqParams(obj, apiLocale);
    }

    fqParams.forEach(fq => paramsFq.append("fq", fq));

    const profileName = `profile_${lwAppName}_Search`;
    const rows = 0;
    const query = `q=${encodeURIComponent(search)}&profilename=${profileName}&fl=${SEARCH_PDP_FL.join(",")}&rows=${rows}&collections=${lwAppName}&${paramsFq.toString()}`;
    const url = `${APIM_BASE_URL}${searchPLPEndPoint}?${query}`;
    apim
      .get(url)
      .then(response => {
        if (response?.data) {
          extractDataAndFillTabs(response.data);
        }
      })
      .catch(err => {
        setIsLoading(false);
      });
  }, [search, defaultLocale]);

  useEffect(() => {
    if (search) {
      executeSearchPLP();
    }
  }, [search, executeSearchPLP]);

  const handleMouseOver = id => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
    setSelectedSkuId("");
  };

  const handleModalOpen = (sku = "") => {
    setModalOpen(true);
    // setIsHovering(false)
    setSelectedSkuId(sku);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleRelavanceSort = (value = "") => {
    const postBody = { ...apiRequestBody };
    delete postBody.orderby;
    delete postBody.sort;
    handleFilterFromQuery(postBody, "orderby");
    setSelectedSortOrder(value);
    needApiCall.current = false;
    filtersAppliedRef.current = true;
  };

  const orderBySelect = async e => {
    const { target: { value = "" } = {} } = e;
    const postBody = { ...apiRequestBody };
    setIsLoading(true);

    if (value === " ") {
      handleRelavanceSort(value);
      return;
    }

    if (value) {
      postBody.orderby = value?.split(" ")[0];
      postBody.sort = value?.split(" ")[1];
      handleFilterFromQuery(postBody);
    } else {
      delete postBody.orderby;
      delete postBody.sort;
      handleFilterFromQuery(postBody, "orderby");
    }
    value
      ? setSelectedSortOrder(`${postBody.orderby} ${postBody.sort}`)
      : setSelectedSortOrder("");
    needApiCall.current = false;
    filtersAppliedRef.current = true;
  };

  const executePLPClientSide = useCallback(
    (fqParams = [], sortQuery = "", start = 0, rows = 30) => {
      const paramsFq = new URLSearchParams();
      fqParams.forEach(fq => paramsFq.append("fq", fq));

      if (sortQuery) paramsFq.append("sort", sortQuery);

      if (search) paramsFq.append("q", `${search}`);

      const type = determineActiveType();
      if (
        activeTabRef.current &&
        (activeTabRef.current === "ARTICLES AND VIDEOS" || type === "articles")
      ) {
        executeArticlesAndVideosSearch(start, rows, sortQuery);
        return;
      }
      paramsFq.append("fq", `product_s:("Yes")`);

      if (categoryResult) {
        const obj = {
          ProductCategory_s: categoryResult,
        };
        const categoryParams = buildTaggedFilter(obj);
        paramsFq.append("fq", categoryParams);
      }

      if (!needApiCall.current) {
        setIsLoading(true);
        setPlpStateData([]);
      }
      // return
      const profileName = `profile_${lwAppName}_Search`;
      const url = `${searchPLPEndPoint}?profilename=${profileName}&collections=${lwAppName}&${paramsFq.toString()}&fl=${SEARCH_PDP_FL.join(",")}&rows=${rows}&start=${start}`;
      apim
        .get(url)
        .then(async res => {
          const { data = null } = res || {};
          if (data) {
            prepareProductData(data);
          }
        })
        .catch(err => {
          console.log(err, "error in client side plp");
          setIsLoading(false);
        });
    },
    [search]
  );

  const fetchMoreProducts = () => {
    let hasMoreData = false;
    if (
      activeTabRef.current &&
      activeTabRef.current === "ARTICLES AND VIDEOS"
    ) {
      hasMoreData = hasMoreArticles.current =
        articlesDataRef.current < totalArticles.current;
    } else if (activeTabRef.current && activeTabRef.current === "SPEC") {
      hasMoreData = hasMoreSpecData.current =
        specDataRef.current < totalSpecData.current;
    } else {
      hasMoreData = hasMore.current =
        productsRef.current.length < totalProductCountRef.current;
    }
    if (hasMoreData) {
      needApiCall.current = true;
      setStart(prevStart => prevStart + rows);
    }
  };

  const fetchProducts = useCallback(() => {
    let rowsVal = 30;
    let startModified = 0;
    const fqParams = buildFqParams(filterSelectedOnAction, defaultLocale);
    let sortQuery = "";
    if (selectedSortOrder) {
      if (selectedSortOrder !== " ") {
        sortQuery = selectedSortOrder;
      }
    }
    if (fqParams.length > 1 || sortQuery) {
      rowsVal = rowsVal + start;
    } else {
      startModified = start;
    }
    executePLPClientSide(fqParams, sortQuery, startModified, rowsVal);
  }, [
    filterSelectedOnAction,
    selectedSortOrder,
    start,
    defaultLocale,
    executePLPClientSide,
  ]);

  useEffect(() => {
    const checkAndFetch =
      hasAnyPrefilters.current ||
      needApiCall.current ||
      filtersAppliedRef.current;

    if (checkAndFetch) {
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProducts]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const hasMoreData =
          activeTabRef.current && activeTabRef.current === "ARTICLES AND VIDEOS"
            ? hasMoreArticles.current
            : activeTabRef.current && activeTabRef.current === "SPEC"
              ? hasMoreSpecData.current
              : hasMore.current;
        if (entry.isIntersecting && !isLoading && hasMoreData) {
          fetchMoreProducts();
        }
      },
      { rootMargin: "100px" }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [isLoading, activeTab]);

  const normalize = v => String(v).trim();

  const filterSelect = async (filterRequest = {}, filterCount = {}) => {
    const entries = Object.entries(filterRequest);
    const [field, rawValue] = entries[0];

    const filterObj = { ...filterSelectedOnAction };
    const apiRequestBodyObj = { ...apiRequestBody };
    const selectedFilterCountObj = { ...selectedFilterCount };
    const filterCountEntries = Object.entries(filterCount);
    const [aliasFieldName, countValue] = filterCountEntries[0];
    if (rawValue === "") {
      delete filterObj[field];
      delete apiRequestBodyObj[aliasFieldName];
      delete selectedFilterCountObj[aliasFieldName];
      setFilterSelectedOnAction(filterObj);
      setApiRequestBody(apiRequestBodyObj);
      setSelectedFilterCount(selectedFilterCountObj);
      handleFilterFromQuery(apiRequestBodyObj, aliasFieldName);
    } else {
      filterObj[field] = rawValue;
      setFilterSelectedOnAction(filterObj);

      apiRequestBodyObj[aliasFieldName] = rawValue;
      selectedFilterCountObj[aliasFieldName] = countValue;
      setApiRequestBody(apiRequestBodyObj);
      setSelectedFilterCount(selectedFilterCountObj);
      handleFilterFromQuery(apiRequestBodyObj);
    }
    filtersAppliedRef.current = true;
    needApiCall.current = false;
  };
  // Extracted cards/spec section rendering
  let cardsSection = null;
  if (activeTab === "PRODUCTS") {
    cardsSection = (
      <div className="flex content-start items-start md:ml-[30px] mb-[40px] mt-[20px] pb-[20px] md:w-3/4 flex-wrap">
        {/* Repeater div 1 */}
        {plpStateData.length ? (
          plpStateData?.map(item => (
            <NewPLPCard
              handleMouseOut={() => handleMouseOut()}
              handleMouseOver={e => handleMouseOver(e)}
              isHovering={isHovering}
              selectedSkuId={selectedSkuId}
              item={item}
              handleModalOpen={(sku = "") => handleModalOpen(sku)}
              handleModalClose={() => handleModalClose()}
              modalOpen={modalOpen}
              key={item?.sku}
              locale={defaultLocale}
              displayCompare={!search}
            />
          ))
        ) : (
          <div>{staticLabelsPLP[defaultLocale]?.noProductFound}</div>
        )}
      </div>
    );
  } else if (activeTab === "SPEC") {
    cardsSection = (
      <div className="float-right lg:mx-10 md:mx-10 my-0 py-10px pb-[20px] w-full md:w-2/3 lg:w-2/3">
        <SpecCard productValueArray={plpSpecData} locale={defaultLocale} />
      </div>
    );
  } else {
    cardsSection = (
      <div className="float-right lg:mx-10 md:mx-10 my-0 px-[10px] py-[10px] pb-[20px] w-full md:w-2/3 lg:w-2/3">
        <ArticleVideos data={articlesData} />
      </div>
    );
  }
  const noResults = tabState.filter(tab => tab.count > 0).length === 0;
  return (
    <>
      {isLoading && <Loader loading={isLoading} />}
      {noResults ? null : (
        <section
          id="plp-section"
          className="max-w-screen-lg mx-auto bg-white text-[#232323] overflow-auto"
        >
          <div className="flex justify-between flex-wrap md:flex-nowrap">
            {categoryResult ? (
              <PLPHeader
                productCount={totalProductCountForHeader}
                pageHeading={categoryResult}
                locale={defaultLocale}
              />
            ) : (
              <div className="flex flex-col pt-[50px] md:pb-[22px] m-[10px] border-b border-[#bebebe] md:border-b-0 w-full"></div>
            )}

            <PLPOrderBySelect
              orderBySelect={e => orderBySelect(e)}
              sortingArray={selectOptions[defaultLocale]}
              selectedSortOrder={selectedSortOrder}
            />
            {/* order by select -- end  */}
          </div>
          <div className="flex flex-wrap md:flex-nowrap w-full flex-col md:flex-row md:gap-[60px]">
            {activeTab === "ARTICLES AND VIDEOS" ? (
              <>
                <div className="hidden md:w-1/4 flex flex-col pl-[10px] mb-[38px] md:block">
                  {selectedContentType?.value ||
                  contentTypeFilterOptions.length ? (
                    <ContentTypeFilter
                      items={contentTypeFilterOptions}
                      handleSelectedContentType={handleSelectedContentType}
                      selectedContentType={selectedContentType}
                    />
                  ) : (
                    <span>No Filters</span>
                  )}
                </div>
                <div className="block appearance-none w-full py-[10px] px-[10px] pr-[10px] rounded leading-normal focus:outline-none focus:bg-white focus:border-gray-500 md:hidden">
                  <button
                    className="m-0 mb-[20px] text-[16px] outline-none bg-white text-[#232323] inline-block border border-solid border-gray-300 rounded-md font-[400] leading-[16px] text-center w-full py-5 px-8 cursor-pointer font-[helveticaRoman,_Helvetica,_sans-serif]"
                    onClick={toggleMobileContentTypeFilter}
                  >
                    <span>{staticLabelsPLP[locale]?.buttonMDLabel}</span>
                  </button>
                  {showMobileContentTypeFilter && (
                    <div className="w-full flex flex-col pl-[10px]">
                      <ContentTypeFilter
                        items={contentTypeFilterOptions}
                        handleSelectedContentType={handleSelectedContentType}
                        selectedContentType={selectedContentType}
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <FilterContent
                filterOptions={mappedFilterFacets}
                slug={categoryResult ? slug : ""}
                filterSelect={filterSelect}
                selectedFilter={apiRequestBody}
                selectedFilterCount={selectedFilterCount}
                locale={defaultLocale}
                totalProductCount={appliedFiltersCount}
                setSelectedFilterCount={setSelectedFilterCount}
                categoryConfig={categoryResult}
                pageType="search"
                search={search}
                categoryArr={categoryArr}
                showAll={activeTab !== "PRODUCTS"}
              />
            )}
            {/* cards div */}
            {cardsSection}
          </div>
        </section>
      )}
      <div ref={loadMoreRef} />
      <BackToTop topHeight={0} localeProp={defaultLocale} />
    </>
  );
};

SearchProductList.propTypes = {
  locale: PropTypes.string,
  plpData: PropTypes.object,
  facetLabelsState: PropTypes.object,
  categoryConfig: PropTypes.string,
  params: PropTypes.object,
  search: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  totalProductCount: PropTypes.number,
  setTotalProductCount: PropTypes.func,
  activeTab: PropTypes.string,
  tabState: PropTypes.array,
  setTabState: PropTypes.func,
};

export default SearchProductList;
